import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import InstallPrompt from './components/InstallPrompt';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import BeneficiariesPage from './pages/BeneficiariesPage';
import DistributionPage from './pages/DistributionPage';
import DistributionHistoryPage from './pages/DistributionHistoryPage';
import CheckInPage from './pages/CheckInPage';
import SharedPortalPage from './pages/SharedPortalPage';
import WaitingListPage from './pages/WaitingListPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#4A5D4E] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#595959]">Caricamento...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="w-12 h-12 border-4 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Check-in pages (public for volunteers) */}
      <Route path="/check-in" element={<CheckInPage />} />
      <Route path="/check-in/:beneficiaryId" element={<CheckInPage />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/beneficiaries" element={
        <ProtectedRoute>
          <BeneficiariesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/distribution" element={
        <ProtectedRoute>
          <DistributionPage />
        </ProtectedRoute>
      } />
      
      <Route path="/distribution-history" element={
        <ProtectedRoute>
          <DistributionHistoryPage />
        </ProtectedRoute>
      } />
      
      <Route path="/shared-portal" element={
        <ProtectedRoute>
          <SharedPortalPage />
        </ProtectedRoute>
      } />

      <Route path="/waiting-list" element={
        <ProtectedRoute>
          <WaitingListPage />
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute>
          <UsersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <InstallPrompt />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #E5E5E5',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
